import React from 'react'
import { Button, ErrorForm, HeaderPage, InputForm, SwitchDefault } from '@components/index'
import { useNavigate, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useYupValidation } from '@hooks/index'
import userManagementSchema from '@yupSchemas/userManagement/userManagementSchema'
import { ICreateUpdateUser } from '@interfaces/userManagement/ICreateUpdateUser'
import { toast } from 'react-toastify'
import { errorHandler } from '@services/index'
import UserManagementService from '@services/requests/userManagementService'
import { SpinnerLoader } from '@assets/icons'
import { USER_STATUS } from '@interfaces/userManagement/IUserManagement'
import { FormControlLabel } from '@mui/material'
import { USER_ROLE } from '@interfaces/userManagement/enums/userRole'

export default function CreateUpdateUsers(): React.ReactElement {
    const [isLoading, setIsLoading] = React.useState(false)
    const navigate = useNavigate()
    const params = useParams()
    const id = params?.id

    interface IFormCreateUpdateUserAdmin extends Omit<ICreateUpdateUser, 'status'> {
        status?: boolean
    }

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<IFormCreateUpdateUserAdmin>({
        resolver: useYupValidation(userManagementSchema),
        mode: 'all',
        defaultValues: {
            name: '',
            email: '',
            status: false
        },

    })

    async function submit(values: IFormCreateUpdateUserAdmin): Promise<void> {
        setIsLoading(true)
        try {
            let message: string
            if (id) {
                const response = await UserManagementService.editUser(Number(params.id), {
                    ...values,
                    status: values.status ? USER_STATUS.ACTIVE : USER_STATUS.INACTIVE
                })
                message = response.message
            } else {
                delete values.status
                const body = { ...values } as ICreateUpdateUser
                const response = await UserManagementService.createUser({ ...body, role: USER_ROLE.ADMIN })
                message = response.message
            }
            toast(message, { type: 'success' })
            navigate('/administradores')
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    async function getUserData(id: number): Promise<void> {
        setIsLoading(true)
        try {
            const response = await UserManagementService.getUser(id)
            reset({
                name: response.name,
                email: response.email,
                status: response.status === USER_STATUS.ACTIVE
            })
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    React.useEffect(() => {
        if (params?.id) {
            getUserData(Number(params.id))
        }
    }, [])

    return (
        <div className="card">
            <div className="create-update-users">
                <div className='box-header'>
                    <HeaderPage title={params?.id ? 'Editar Usuário' : 'Novo Usuário'} />
                </div>
                <form onSubmit={handleSubmit(submit)}>
                    <div className='box-header'>
                    </div>
                    <div className='box-inputs'>
                        <div className="box-controller">
                            <Controller
                                name="name"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="Nome"
                                            placeholder='Digite o nome do usuário'
                                            name="name"
                                            value={value}
                                            inputProps={{ maxLength: 50 }}
                                            onChange={onChange}
                                            size='small'

                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.name?.message as string} />
                        </div>
                        <div className="box-controller">
                            <Controller
                                name="email"
                                control={control}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <InputForm
                                            label="E-mail"
                                            placeholder='example@email.com'
                                            name="email"
                                            value={value}
                                            onChange={onChange}
                                            size='small'
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    )
                                }}
                            />
                            <ErrorForm error={errors.email?.message as string} />
                        </div>
                    </div>
                    {id &&
                        <div className='box-controller'>
                            <Controller
                                name='status'
                                control={control}
                                defaultValue={true}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel style={{ margin: 0 }} labelPlacement='top' value='top' control={<SwitchDefault value={value} onChange={onChange} checked={value} />} label='Status' />
                                            <label style={{ fontSize: '0.8rem', marginTop: '1.5rem', fontFamily: 'Open sans' }}>{value ? 'Ativo' : 'Inativo'}</label>
                                        </div>
                                    )
                                }
                                }
                            />
                        </div>
                    }
                    <div className="box-buttons">
                        <Button type="button" secondary placeholder={'Cancelar'} onClick={() => navigate('/administradores')} />
                        <Button type="submit" isLoading={isLoading} placeholder={'Salvar'} />
                    </div>
                    {isLoading ? (
                        <div className="loader-page">
                            <SpinnerLoader />
                        </div>
                    ) : (
                        <></>
                    )}
                </form>
            </div>
        </div>
    )
}